import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { Divider } from "@src/appV2/redesign/system/ui-components";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { ClockOutAdjustmentNoticePill } from "./ClockOutAdjustmentNoticePill";
import { TimesheetTimeEntryRow } from "./TimesheetTimeEntryRow";
import { TimesheetTotalsFooter } from "./TimesheetTotalsFooter";

interface TimesheetTimeEntriesProps {
  /** Hide pencil icons when editing should be disabled (e.g., timekeeping integrations) */
  hidePencilIcons: boolean;
  disabled?: boolean;
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  onEditClockIn: () => void;
  onEditBreak: () => void;
  onEditClockOut: () => void;
  formatBreakDisplay: () => string;
  /** Pass the original time when clock-in has been adjusted from the recorded value */
  originalClockInTime?: Date;
  /** Pass the original time when clock-out has been adjusted from the recorded value */
  originalClockOutTime?: Date;
  /** Override the default "Original clock out" sub-label (e.g. "Scheduled clock out" for shift extensions). */
  originalClockOutLabel?: string;
  /**
   * When true, renders a yellow warning pill under the clock-out row informing the
   * worker that their clock-out time was adjusted to the scheduled shift end because
   * they did not complete the shift extension flow.
   */
  showClockOutAdjustmentNotice?: boolean;
  /** Pass the original formatted break string when break has been adjusted */
  originalBreakDisplay?: string;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown below the Total row when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
  /** Pre-formatted dollar string (e.g. "$281.84") shown as "Paid out via InstantPay". */
  paidOutAmount?: string;
  /** Pre-formatted dollar string (e.g. "+$2.84") shown as "Pending payment". */
  pendingPaymentAmount?: string;
}

export function TimesheetTimeEntries(props: TimesheetTimeEntriesProps) {
  const {
    hidePencilIcons,
    disabled = false,
    displayTimesheet,
    workplaceTimezone,
    onEditClockIn,
    onEditBreak,
    onEditClockOut,
    formatBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    originalClockOutLabel,
    showClockOutAdjustmentNotice = false,
    originalBreakDisplay,
    verifiedTimesheet,
    paidOutAmount,
    pendingPaymentAmount,
  } = props;

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock in"
        originalValue={
          isDefined(originalClockInTime) ? formatWorkplaceTime(originalClockInTime) : undefined
        }
      >
        <Button
          variant="outlinedHeavy"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockIn}
        >
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Button>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow iconType="break" label="Break" originalValue={originalBreakDisplay}>
        <Button
          variant="outlinedHeavy"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditBreak}
        >
          {formatBreakDisplay()}
        </Button>
      </TimesheetTimeEntryRow>

      <Stack spacing={2}>
        <TimesheetTimeEntryRow
          iconType="clock"
          label="Clock out"
          originalValue={
            isDefined(originalClockOutTime) ? formatWorkplaceTime(originalClockOutTime) : undefined
          }
          originalValueLabel={originalClockOutLabel}
        >
          <Button
            variant="outlinedHeavy"
            size="small"
            disabled={disabled}
            endIconType={hidePencilIcons ? undefined : "pencil"}
            onClick={onEditClockOut}
          >
            {formatWorkplaceTime(displayTimesheet.clockOutTime)}
          </Button>
        </TimesheetTimeEntryRow>

        {showClockOutAdjustmentNotice && <ClockOutAdjustmentNoticePill />}
      </Stack>

      <Divider />

      <TimesheetTotalsFooter
        displayTimesheet={displayTimesheet}
        verifiedTimesheet={verifiedTimesheet}
        paidOutAmount={paidOutAmount}
        pendingPaymentAmount={pendingPaymentAmount}
      />
    </Stack>
  );
}
